import { useQuery } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  Chip,
  type ChipProps,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useCallback } from "react";
import { useTranslate } from "react-admin";

import {
  MarketingDeliveryProviderType,
  MarketingDnsRecordStatus,
  MarketingSendingDomainDocument,
  type MarketingSendingDomainQuery,
} from "../generated/graphql";
import { formatCrmDateTime } from "../lib/crm";
import { space } from "../theme/designTokens";
import { SettingsSection } from "./SettingsSection";

const STATUS_COLOR: Record<MarketingDnsRecordStatus, ChipProps["color"]> = {
  [MarketingDnsRecordStatus.Verified]: "success",
  [MarketingDnsRecordStatus.Inherited]: "info",
  [MarketingDnsRecordStatus.Missing]: "error",
  [MarketingDnsRecordStatus.Mismatch]: "warning",
  [MarketingDnsRecordStatus.Unknown]: "default",
};

// What DNS returned is only worth showing when it differs from, or replaces, what is asked for.
const SHOWS_FOUND: readonly MarketingDnsRecordStatus[] = [
  MarketingDnsRecordStatus.Mismatch,
  MarketingDnsRecordStatus.Inherited,
];

const COLUMN_KEYS = ["purpose", "type", "name", "value", "status"] as const;

const MONOSPACE_WRAP = { fontFamily: "monospace", overflowWrap: "anywhere" } as const;

type MarketingSendingDomainProps = {
  provider: MarketingDeliveryProviderType | null;
};

export const MarketingSendingDomain = ({ provider }: MarketingSendingDomainProps) => {
  const translate = useTranslate();
  const { data, loading, error, refetch } = useQuery<MarketingSendingDomainQuery>(MarketingSendingDomainDocument, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });
  const handleRecheck = useCallback(() => {
    // A failed refetch shows in the query's error.
    refetch().catch(() => undefined);
  }, [refetch]);
  const sendingDomain = data?.marketingSendingDomain;

  let content = null;
  if (error) content = <Alert severity="error">{translate("marketingDelivery.dns.loadError")}</Alert>;
  else if (loading) content = <CircularProgress aria-label={translate("marketingDelivery.dns.loading")} />;
  else if (sendingDomain)
    content = (
      <TableContainer>
        <Table size="small" data-testid="marketing-dns-table">
          <TableHead>
            <TableRow>
              {COLUMN_KEYS.map(key => (
                <TableCell key={key}>{translate(`marketingDelivery.dns.columns.${key}`)}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sendingDomain.records.map(record => (
              <TableRow key={`${record.purpose}-${record.name}`}>
                <TableCell>{translate(`marketingDelivery.dns.purposes.${record.purpose}`)}</TableCell>
                <TableCell>{record.type}</TableCell>
                <TableCell sx={MONOSPACE_WRAP}>{record.name}</TableCell>
                <TableCell>
                  <Box sx={MONOSPACE_WRAP}>{record.value}</Box>
                  {SHOWS_FOUND.includes(record.status) && record.found.length > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={MONOSPACE_WRAP} component="div">
                      {translate("marketingDelivery.dns.found", { values: record.found.join(", ") })}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={STATUS_COLOR[record.status]}
                    label={translate(`marketingDelivery.dns.statuses.${record.status}`)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );

  return (
    <Box data-testid="marketing-sending-domain">
      <SettingsSection title={translate("marketingDelivery.dns.title")}>
        <Typography color="text.secondary">{translate("marketingDelivery.dns.description")}</Typography>
        {provider === MarketingDeliveryProviderType.Fake && (
          <Typography variant="body2" color="text.secondary">
            {translate("marketingDelivery.dns.fakeNote")}
          </Typography>
        )}
        {content}
        <Stack direction="row" alignItems="center" gap={space.xs}>
          <Button variant="outlined" onClick={handleRecheck} disabled={loading}>
            {translate("marketingDelivery.dns.recheck")}
          </Button>
          {sendingDomain && !loading && (
            <Typography variant="caption" color="text.secondary">
              {translate("marketingDelivery.dns.checkedAt", { time: formatCrmDateTime(sendingDomain.checkedAt) })}
            </Typography>
          )}
        </Stack>
      </SettingsSection>
    </Box>
  );
};
